import React, { useState, useEffect, useMemo, useCallback, ReactNode, useContext, ComponentType, ButtonHTMLAttributes } from 'react';
import { styled } from '@mui/material';

import Toolbar from '@/components/Toolbar';
import FilterTabsBar from '@/components/FilterTabsBar';
import FilterTabButton from '@/components/FilterTabButton';
import SearchInput from '@/components/SearchInput';
import TableList, { ColumnMeta } from '@/components/TableList';
import BulkOperationButton from '@/components/BulkOperationButton';
import { PageSection } from './common.controls';

import UserCard from '@/components/UserCard';

import { AccessLevel, UserStatus } from '@bimdata/definitions/const';
import { Participant, User } from '@bimdata/definitions/dto';
import { Button, AccessSelector } from '@/components';

import {
  participants as participantList,
  invitations as invitationList
} from '@/loaders/project';
import {
  editParticipants,
  excludeParticipants,
  editInvitations,
  revokeInvitations,
  setParticipantsBlocked
} from '@/actions/project';

import { RootState, useAppSelector } from '@/state/store';
import { Invitation } from '@/state/types/invitation';
import { getUserLabel } from '@/contracts';
import { createSelector } from '@reduxjs/toolkit';
import DateCard from '@/components/DateCard';
import ConfirmUserIcon from '@/components/icons/ConfirmUser';
import BlockUserIcon from '@/components/icons/BlockUser';
import DeleteUserIcon from '@/components/icons/DeleteUser';
import hinted from '@/components/Hinted';
import { avatar } from '@/loaders/user';
import t from '@/utils/translate';
import { MembersSorting, MembersSortingBy } from '@/state/authentication.slice';
import { saveLastProjectUpdateMembersSorting } from '@/actions/client';
import { AmountsLabel } from '@/components/AmountLabel';
import { escRegExpSpecChars } from '@/utils/regexp';
import Prompt from '@/components/Prompt';

const ParticipantButton = hinted(
  styled(Button)({
    padding: '0px',
    width: '32px',
    height: '32px',
    minWidth: 'unset',
    minHeight: 'unset',
    fontSize: '20px',
    ['&:not(:first-of-type)']: {
      marginLeft: '12px',
    },
    ['&:hover']: {
      backgroundColor: 'unset',
    },
    '& svg': {
      opacity: 0.5,
    },
    ['&:hover svg']: {
      opacity: 1,
    }
  }),
  {}
);

type ProjectMembersProps = {
  currentUser: User;
  projectId: string;
  ownershipManagement: boolean;
  placeholder: ReactNode;
};

const StyledUserCard = styled(UserCard)(({ hideEmail }) => ({
  ['& div.MuiAvatar-root']: {
    width: '32px',
    height: '32px',
  },
  ['& span.UserCard-fullname']: {
    fontWeight: hideEmail ? 700 : 400,
  }
}));

type StateFilter = 'all' | 'active' | 'blocked' | 'pending';

type Item = (Participant | Invitation);

const idGetter = (i: Item) => !!(i as Participant).user
  ? `user${(i as Participant).user.id}`
  : `inv${(i as Invitation).id}`;

type Group = {
  code: string;
  title: string;
  data: Item[];
}

function attrValue(item: Item, nm: MembersSortingBy) {
  switch (nm) {
    case 'name': return ((item as Participant).user || (item as Invitation).participant)?.fullName || ((item as Invitation).participant as string) || '';
    case 'since': return (item as Participant).since || 0;
    default: return item[nm];
  }
}

type BulkOperationProps = {
  items: Array<Item>;
  projectId: string;
  currentUser: User;
};

type BulkAccessSelectorProps = BulkOperationProps & {
  ownershipManagement: boolean;
  transferOwning: (u: User) => void
};

const BulkAccessSelectorView = styled(AccessSelector)({
  height: '30px',
  width: '126px',

  '& .MuiInputBase-root': {
    background: '#FFFFFF',
    border: '1px solid #DFE1E6',
    padding: '5px 4px 5px 10px',
    borderRadius: '4px',

    '& fieldset': {
      border: 'none',
    },

    '&:hover fieldset': {
      border: 'none',
    },

    '&.Mui-focused fieldset': {
      border: 'none',
    },

    '&.Mui-disabled': {
      filter: 'saturate(0) opacity(0.85)',
      cursor: 'default',
    },
  },

  '& .MuiSvgIcon-root': {
    color: '#C1C7D0',
    fontSize: '20px',
    right: '4px',
  },

  '& label.MuiFormLabel-root': {
    color: '#6B778C',
    fontSize: '13px',
    fontWeight: 400,
    left: '10px',
    /*
    '&.MuiInputLabel-shrink': {
      left: '10px',
      top: 'auto',
      transform: 'none',
    }
    */
  }
});

const BulkAccessSelector = ({ items, ownershipManagement, currentUser, projectId, transferOwning }: BulkAccessSelectorProps) => {
  const candidates = useMemo<Item[]>(() => {
    return items.filter((i) => {
      return !(
        i.access === AccessLevel.OWNER
        || !!(i as Invitation).id
        || (currentUser.id === (i as Participant).user?.id)
        || (i as Participant).blocked
      );
    });
  }, [ items, currentUser ]);

  const changeHandler = useCallback(async (level: AccessLevel) => {
    if (
      candidates.length === 1
      && !(candidates[0] as Invitation).id
      && level === AccessLevel.OWNER
    ) {
      transferOwning((candidates[0] as Participant).user);
      return;
    }

    const { invs, partisIds } = candidates.reduce((acc, item) => {
      if (!!(item as Invitation).id) {
        acc.invs.push(item as Invitation);
      } else {
        acc.partisIds.push((item as Participant).user.id);
      }
      return acc;
    }, {
      invs: new Array<Invitation>(),
      partisIds: new Array<string>()
    });

    if (invs.length) {
      await editInvitations(projectId, invs, level);
    }
    if (partisIds.length) {
      await editParticipants(projectId, partisIds, level);
    }
  }, [ candidates, transferOwning ]);

  return <BulkAccessSelectorView
    excludeLevels={(!ownershipManagement || (candidates.length > 1)) ? [AccessLevel.OWNER] : []}
    value={null}
    label={t('participant.action.bulk.access')}
    focused={false}
    onChange={changeHandler}
    disabled={!candidates.length}
  />;
};

type BulkSetParticipantBlockedProps = BulkOperationProps & {
  title: string,
  value: boolean,
  icon: ReactNode
};

const BulkSetParticipantBlockedButton = ({ items, projectId, title, value, icon, currentUser }: BulkSetParticipantBlockedProps) => {
  const candidates = useMemo<Participant[]>(() => {
    return items.filter((i) => (
      !!(i as Participant).user
      && i.access !== AccessLevel.OWNER
      && currentUser.id !== (i as Participant).user.id
      && value !== (i as Participant).blocked
    )) as Participant[];
  }, [ items, currentUser, value ]);

  const handler = useCallback(
    () => setParticipantsBlocked(projectId, candidates.map((p) => p.user.id), value),
    [ candidates, projectId, value ]
  );

  return <BulkOperationButton
    onClick={handler}
    disabled={!candidates.length}
  >
    {icon}
    {title}
    {!!candidates.length && <AmountsLabel>{candidates.length}</AmountsLabel>}
  </BulkOperationButton>;
}

type BulkRemovingParticipantProps = Omit<BulkOperationProps, 'projectId'> & {
  onHandle: (candidates: Item[]) => void
};

const BulkRemovingParticipantButton = ({ items, currentUser, onHandle }: BulkRemovingParticipantProps) => {
  const candidates = useMemo<Item[]>(() => {
    return items.filter((i) => (
      (!!(i as Participant).user
        && i.access !== AccessLevel.OWNER
        && currentUser.id !== (i as Participant).user.id
      )
      || !!(i as Invitation).participant
    ));
  }, [ items, currentUser ]);

  const handler = useCallback(async () => onHandle(candidates), [ candidates, onHandle ]);

  return <BulkOperationButton
    onClick={handler}
    disabled={!candidates.length}
  >
    <DeleteUserIcon size={20} color="#DE350B"/>
    {t('participant.action.bulk.exclude')}
    {!!candidates.length && <AmountsLabel>{candidates.length}</AmountsLabel>}
  </BulkOperationButton>;
};

const defaultSorting = ['name', 'A'] as MembersSorting;

export default ({ currentUser, projectId, ownershipManagement, placeholder }: ProjectMembersProps) => {
  const { participants, invitations, avatars, sorting } = useAppSelector(
    createSelector(
      [
        (state: RootState) => state.projects.current?.participants,
        (state: RootState) => state.projects.current?.invitations,
        (state: RootState) => state.user.avatars,
        (state: RootState) => state.authentication.clientState.lastProjectUpdateMembersSorting || defaultSorting,
      ],
      (participants, invitations, avatars, sorting) => ({
        participants: participants || null,
        invitations: invitations || null,
        avatars,
        sorting
      })
    )
  );

  const [stateFilter, setStateFilter] = useState<StateFilter>('all');
  const [search, setSearch] = useState<string>('');
  const [removingParticipant, setRemovingParticipant] = useState<User | null>(null);
  const [removingInvitation, setRemovingInvitation] = useState<Invitation | null>(null);
  const [bulkRemovingList, setBulkRemovingList] = useState<Item[]>([]);
  const [candidateToOwning, setCandidateToOwning] = useState<User | null>(null);
  const [selected, setSelected] = useState<Item[]>([]);

  useEffect(() => {
    if (!invitations) {
      invitationList(projectId);
    }
  }, [ invitations ]);

  useEffect(() => {
    if (!participants) {
      participantList(projectId);
    }
  }, [ participants ]);

  const revokeInvitationCallback = useCallback(async () => {
    if (removingInvitation) {
      await revokeInvitations(projectId, [removingInvitation]);
    }
    setRemovingInvitation(null);
  }, [ projectId, removingInvitation ]);

  const cancelInvitationRevoking = useCallback(() => setRemovingInvitation(null), []);

  const transferOwningCallback = useCallback(async () => {
    if (candidateToOwning) {
      await editParticipants(projectId, [candidateToOwning.id], AccessLevel.OWNER);
    }
    setCandidateToOwning(null);
  }, [ projectId, candidateToOwning ]);

  const cancelOwningTransferring = useCallback(() => setCandidateToOwning(null), []);

  const excludeParticipantCallback = useCallback(async () => {
    if (removingParticipant) {
      await excludeParticipants(projectId, [removingParticipant.id]);
    }
    setRemovingParticipant(null);
  }, [ projectId, removingParticipant ]);

  const cancelParticipantExcluding = useCallback(() => setRemovingParticipant(null), []);

  const bulkExcludeParticipants = useCallback(async () => {
    const { invs, partisIds } = bulkRemovingList.reduce((acc, item) => {
      if (!!(item as Participant).user) {
        acc.partisIds.push((item as Participant).user.id);
      }
      if ((item as Invitation).participant) {
        acc.invs.push(item as Invitation);
      }
      return acc;
    }, {
      invs: new Array<Invitation>(),
      partisIds: new Array<string>()
    });

    if (invs.length) {
      await revokeInvitations(projectId, invs);
    }
    if (partisIds.length) {
      await excludeParticipants(projectId, partisIds);
    }
    setBulkRemovingList([]);
  }, [ projectId, bulkRemovingList ]);

  const cancelBulkParticipantExcluding = useCallback(() => setBulkRemovingList([]), []);

  const onSearchChange = useCallback((v: string) => {
    setSearch(v);
  }, []);

  const sortingHandler = useCallback(async (by: MembersSortingBy, order: 'A' | 'D') => {
    await saveLastProjectUpdateMembersSorting([by, order]);
  }, []);

  const getUser = (item: Invitation | Participant): User => {
    if ((item as Participant).user) return (item as Participant).user;
    if (typeof (item as Invitation).participant === 'string') {
      const email = ((item as Invitation).participant as string);
      return {
        id: '',
        avatar: null,
        fullName: email,
        email,
        status: UserStatus.INACTIVE
      };
    }
    return (item as Invitation).participant as User;
  }

  const ensureRow = useCallback((item: Item) => {
    const user = getUser(item);
    if (user.avatar && (avatars[user.id]?.source || null) === null) {
      avatar(user);
    }
  }, [avatars]);

  const columns = useMemo<ColumnMeta[]>(() => {
    const sre = search ? new RegExp(escRegExpSpecChars(search), 'i') : undefined;
    return [
    {
      attrName: 'name',
      title: t('participant.column.name'),
      dataGetter: (item: Item) => {
        const user = getUser(item);
        return (
          <StyledUserCard
            profile={user.id === currentUser.id ? currentUser : user}
            hideEmail={user.id === currentUser.id ? true : undefined}
            blocked={user.status === UserStatus.BLOCKED || user.status === UserStatus.GONE}
            avatar={(!!user.avatar && avatars[user.id]?.source) || false}
            highlight={sre}
          />);
      },
    },
    {
      attrName: 'access',
      title: t('participant.column.access'),
      dataGetter: (item: Item) => {
        const isOwner = item.access === AccessLevel.OWNER;
        return (
        <AccessSelector
          excludeLevels={!ownershipManagement ? [AccessLevel.OWNER] : []}
          value={item.access}
          onChange={async (level) => {
            if (!!(item as Invitation).id) {
              editInvitations(projectId, [item as Invitation], level);
            } else {
              if (level === AccessLevel.OWNER) {
                setCandidateToOwning((item as Participant).user);
                return;
              }
              editParticipants(projectId, [(item as Participant).user.id], level);
            }
          }}
          disabled={isOwner || !!(item as Invitation).id || (currentUser.id === (item as Participant).user?.id) || (item as Participant).blocked}
          hint={currentUser.id === (item as Participant).user?.id
            ? t('participant.hint.selfAccess')
            : (isOwner
              ? t('participant.hint.noaccess')
              : ((item as Participant).blocked
                ? t('participant.hint.blocked')
                : null
              )
            )
          }
          sx={{
            flexShrink: 0,
            height: '30px',
            width: '155px',
            ...((item as Participant).blocked ? { textDecoration: 'line-through' } : {})
          }}
        />);
      },
    },
    {
      width: 130,
      attrName: 'since',
      title: t('participant.column.joined'),
      dataGetter: (item: Item) => (item as Participant).since ? <DateCard value={(item as Participant).since} /> : '-',
      defaultSorting: 'D',
    },
    {
      width: 40,
      type: 'iconic',
      dataGetter: (item: Participant | Invitation) => {
        if ((item as Participant).user) {
          const isOwner = item.access === AccessLevel.OWNER;
          const part = (item as Participant);
          return (
            <>
              {!isOwner && currentUser.id !== part.user.id && part.blocked && (
                <ParticipantButton
                  hint={t('participant.action.activate')}
                  color="success"
                  onClick={() => setParticipantsBlocked(projectId, [part.user.id], false)}
                >
                  <ConfirmUserIcon />
                </ParticipantButton>
              )}
              {!isOwner && currentUser.id !== part.user.id && !part.blocked && (
                <ParticipantButton
                  hint={t('participant.action.block')}
                  color="warning"
                  onClick={() => setParticipantsBlocked(projectId, [part.user.id], true)}
                >
                  <BlockUserIcon />
                </ParticipantButton>
              )}
            </>
          );
        }
      },
    },
    {
      width: 48,
      type: 'iconic',
      dataGetter: (item: Participant | Invitation) => {
        if ((item as Participant).user) {
          const isOwner = item.access === AccessLevel.OWNER;
          const part = (item as Participant);
          return (
            <>
              {!isOwner && currentUser.id !== part.user.id && (
                <ParticipantButton
                  hint={t('participant.action.exclude')}
                  color="error"
                  onClick={() => setRemovingParticipant(part.user)}
                >
                  <DeleteUserIcon />
                </ParticipantButton>
              )}
            </>
          );
        } else if ((item as Invitation).participant) {
          return (
            <ParticipantButton
              hint={t('invitation.action.cancel')}
              color="error"
              onClick={() => setRemovingInvitation(item as Invitation)}
            >
              <DeleteUserIcon />
            </ParticipantButton>
          );
        }
      }
    },
  ];}, [ avatars, search, ownershipManagement ]);

  const sorter = (a: Item, b: Item) => {
    const av = (sorting[1] === 'A') ? attrValue(a, sorting[0]) : attrValue(b, sorting[0]);
    const bv = (sorting[1] === 'A') ? attrValue(b, sorting[0]) : attrValue(a, sorting[0]);
    if (typeof av === 'string' || typeof bv === 'string') {
      return `${av}`.localeCompare(`${bv}`);
    }
    return +av - +bv;
  };

  const checkSearchFilter = (u: User | string): boolean => {
    if (!search) return true;
    const _search = search.toLowerCase();
    if (typeof u === 'string') return  u.toLowerCase().includes(_search);
    return (u as User).fullName.toLowerCase().includes(_search) || (u as User).email.toLowerCase().includes(_search);
  }

  const stats = useMemo<{ active: number, blocked: number, pending: number }>(() => {
    const result = {
      active: 0,
      blocked: 0,
      pending: 0,
    };

    if (!invitations || !participants) return result;

    participants.forEach(({ blocked, user }) => {
      if (!checkSearchFilter(user)) return;
      if (blocked) {
        result.blocked++;
      } else {
        result.active++;
      }
    });

    result.pending = invitations.reduce((s, { participant }) => s + (checkSearchFilter(participant) ? 1 : 0), 0);

    return result;
  }, [participants, invitations, search]);

  const data = useMemo<Group[] | Item[]>(
    () => {
      if (!invitations || !participants) return [];
      switch (stateFilter) {
        case 'active': return participants.filter(({ blocked, user }) => !blocked && checkSearchFilter(user)).sort(sorter);
        case 'blocked': return participants.filter(({ blocked, user }) => !!blocked && checkSearchFilter(user)).sort(sorter);
        case 'pending': return invitations.filter(({ participant }) =>  checkSearchFilter(participant)).sort(sorter);
      }
      return [
        ...(Array.from(participants.reduce<Map<string, Group>>(
          (m, p) => {
            if (!checkSearchFilter(p.user)) return m;
            const code = p.blocked ? 'c' : 'a';
            if (!m.has(code)) {
              m.set(code, { code, title: p.blocked ? t('participant.title.blocked') : t('participant.title.active'), data: [] });
            }
            m.get(code)!.data.push(p);
            return m;
          },
          new Map()
        ).values()).map((g) => {
          g.data.sort(sorter);
          return g;
        })),
        {
          code: 'b',
          title: t('participant.title.pending'),
          data: invitations.filter(({ participant }) =>  checkSearchFilter(participant)).sort(sorter),
        }
      ].sort((a, b) => a.code.localeCompare(b.code))
    },
    [participants, invitations, stateFilter, search, sorting]
  );

  return <PageSection style={{ height: '100%', width: '100%' }}>
    {/*(!invitations || !participants) && <LinearProgress />*/}
    {(invitations && participants) && !((invitations?.length || 0) + (participants?.length || 0)) && placeholder}
    {!!((invitations?.length || 0) + (participants?.length || 0)) && (
      <>
        <Toolbar>
          <FilterTabsBar>
            <FilterTabButton
              value={'all'}
              filter={stateFilter}
              onClick={setStateFilter}
              score={stats.active + stats.blocked + stats.pending}
            >
              {t('participant.list.all')}
            </FilterTabButton>
            <FilterTabButton
              value={'active'}
              filter={stateFilter}
              onClick={setStateFilter}
              score={stats.active}
            >
              {t('participant.list.active')}
            </FilterTabButton>
            <FilterTabButton
              value={'blocked'}
              filter={stateFilter}
              onClick={setStateFilter}
              score={stats.blocked}
            >
              {t('participant.list.blocked')}
            </FilterTabButton>
            <FilterTabButton
              value={'pending'}
              filter={stateFilter}
              onClick={setStateFilter}
              score={stats.pending}
            >
              {t('participant.list.pending')}
            </FilterTabButton>
          </FilterTabsBar>
          <SearchInput
            onChange={onSearchChange}
            value={search}
            placeholder={t('action.search')}
          />
        </Toolbar>
        {(!!invitations || !!participants) && (
          <TableList<Item>
            sorting={sorting}
            columns={columns}
            data={data}
            grouped={(stateFilter === 'all') ? true : undefined}
            hideEmptyGroups
            onRowVisible={ensureRow}
            onSortingChange={sortingHandler}
            idGetter={idGetter}
            isCheckList={true}
            checkListChange={setSelected}
            selectionStatusCaption={t('participant.list.selectionCaption')}
          >
            <BulkAccessSelector
              items={selected}
              currentUser={currentUser}
              projectId={projectId}
              ownershipManagement={ownershipManagement}
              transferOwning={setCandidateToOwning}
            />
            <BulkSetParticipantBlockedButton
              items={selected}
              currentUser={currentUser}
              projectId={projectId}
              title={t('participant.action.bulk.activate')}
              value={false}
              icon={<ConfirmUserIcon size={20} color="#00875A"/>}
            />
            <BulkSetParticipantBlockedButton
              items={selected}
              currentUser={currentUser}
              projectId={projectId}
              title={t('participant.action.bulk.block')}
              value={true}
              icon={<BlockUserIcon size={20} color="#FF991F"/>}
            />
            <BulkRemovingParticipantButton
              items={selected}
              currentUser={currentUser}
              onHandle={setBulkRemovingList}
            />
          </TableList>
        )}
        <Prompt
          open={!!candidateToOwning}
          title={t('participant.title.confirm.transfer')}
          question={candidateToOwning && t('participant.hint.confirm.transfer', { user: getUserLabel(candidateToOwning) }, true)}
          onClose='cancel'
          onEnter='transfer'
          actions={[
            {
              name: 'transfer',
              handler: transferOwningCallback,
              caption: t('participant.action.transfer'),
              style: {
                background: '#0747A6',
                color: '#FFFFFF',
                border: 'none',
              }
            },
            {
              name: 'cancel',
              handler: cancelOwningTransferring,
              caption: t('common.action.cancel'),
            }
          ]}
        />

        <Prompt
          open={!!bulkRemovingList.length}
          title={t('participant.title.confirm.exclude', { count: bulkRemovingList.length })}
          question={
            bulkRemovingList.length && t('participant.hint.confirm.exclude', {
              count: bulkRemovingList.length,
              users: bulkRemovingList.map((i) => {
                if ((i as Participant).user) {
                  return getUserLabel((i as Participant).user);
                }
                if ((i as Invitation).participant === 'string') {
                  return (i as Invitation).participant as string;
                }
                return getUserLabel((i as Invitation).participant as User);
              }).join(', ')
            }, true)
          }
          onClose='cancel'
          onEnter='exclude'
          actions={[
            {
              name: 'exclude',
              handler: bulkExcludeParticipants,
              caption: t('participant.action.bulk.exclude'),
              style: {
                background: '#0747A6',
                color: '#FFFFFF',
                border: 'none',
              }
            },
            {
              name: 'cancel',
              handler: cancelBulkParticipantExcluding,
              caption: t('common.action.cancel'),
            }
          ]}
        />

        <Prompt
          open={!!removingParticipant}
          title={t('participant.title.confirm.exclude', { count: 1 })}
          question={removingParticipant && t('participant.hint.confirm.exclude', { count: 1, users: getUserLabel(removingParticipant) }, true)}
          onClose='cancel'
          onEnter='exclude'
          actions={[
            {
              name: 'exclude',
              handler: excludeParticipantCallback,
              caption: t('participant.action.bulk.exclude'),
              style: {
                background: '#0747A6',
                color: '#FFFFFF',
                border: 'none',
              }
            },
            {
              name: 'cancel',
              handler: cancelParticipantExcluding,
              caption: t('common.action.cancel'),
            }
          ]}
        />

        <Prompt
          open={!!removingInvitation}
          title={t('invitation.title.confirm.cancel')}
          question={
            removingInvitation && 
            t('invitation.hint.confirm.cancel', {
              name: typeof removingInvitation.participant === 'string'
                ? removingInvitation.participant
                : getUserLabel(removingInvitation.participant)
            }, true)
          }
          onClose='cancel'
          onEnter='revoke'
          actions={[
            {
              name: 'revoke',
              handler: revokeInvitationCallback,
              caption: t('invitation.action.revoke'),
              style: {
                background: '#0747A6',
                color: '#FFFFFF',
                border: 'none',
              }
            },
            {
              name: 'cancel',
              handler: cancelInvitationRevoking,
              caption: t('common.action.cancel'),
            }
          ]}
        />
      </>
    )
  }
  </PageSection>;
}